import React, { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useCanvasId,
  useProjectId,
} from "@/utils/utils";
import type { Database } from "@/utils/supabase.types";
import { useNavigate, useSearchParams } from "react-router";
import { useSession } from "@clerk/clerk-react";
import { setUnauthorized } from "@/store/codebaseSlice";
import { useDispatch } from "react-redux";

interface Props {
  onClose?: () => void;
}

type ProjectAccessRequest =
  Database["public"]["Tables"]["project_access_requests"]["Row"];

type Invitation = Database["public"]["Tables"]["project_invitations"]["Row"];

const UnauthorizedModal: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const canvasId = useCanvasId();
  const projectId = useProjectId();
  const { session } = useSession();
  const [fetchingAccessRequest, setFetchingAccessRequest] = useState(true);
  const [accessRequest, setAccessRequest] =
    useState<ProjectAccessRequest | null>(null);
  const [unacceptedInvitation, setUnacceptedInvitation] =
    useState<Invitation | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [hasRequestedAccess, setHasRequestedAccess] = useState(false);

  const dispatch = useDispatch();

  const getAccessEndpoint = (
    projectId: string | null,
    canvasId: string | null,
  ) => {
    return `${import.meta.env.VITE_BACKEND_BASE_URL}/access/?${
      projectId ? `projectId=${projectId}` : `canvasId=${canvasId}`
    }`;
  };

  const fetchProjectAccessRequests = async () => {
    if (!canvasId && !projectId) return;

    const supabaseJWT = await getUnexpiredToken(session);

    setFetchingAccessRequest(true);

    try {
      const endpoint = getAccessEndpoint(projectId, canvasId);

      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${supabaseJWT}` },
      });

      const { data } = res;
      if (data.unacceptedInvitation)
        setUnacceptedInvitation(data.unacceptedInvitation);
      if (data.accessRequest) setAccessRequest(data.accessRequest);
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        e.response?.status === 409 &&
        e.response?.data?.error === "User is already a member"
      ) {
        dispatch(setUnauthorized(false));
        return;
      }

      logAxiosError("Error fetching project access requests", e);
    } finally {
      setFetchingAccessRequest(false);
    }
  };

  useEffect(() => {
    if (session) fetchProjectAccessRequests();
  }, [Boolean(session)]);

  const requestAccess = async () => {
    setHasRequestedAccess(true);

    try {
      const endpoint = getAccessEndpoint(projectId, canvasId);

      const supabaseJWT = await getUnexpiredToken(session);

      await axios.post(
        endpoint,
        {},
        {
          headers: { Authorization: `Bearer ${supabaseJWT}` },
        },
      );

      await fetchProjectAccessRequests();
    } catch (e) {
      toast.error(getAxiosErorrMessage(e));
    }
  };

  const acceptInvitation = async () => {
    if (!unacceptedInvitation) return;

    setAccepting(true);

    const supabaseJWT = await getUnexpiredToken(session);

    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/invitations/accept`,
        { token: unacceptedInvitation.token },
        { headers: { Authorization: `Bearer ${supabaseJWT}` } },
      );
      toast.info("Invitation accepted");
      window.location.reload();
    } catch (e) {
      toast.error(`Couldn't accept invitation: ${getAxiosErorrMessage(e)}`);
    } finally {
      setAccepting(false);
    }
  };

  const isInvited = unacceptedInvitation !== null;
  const canRequestAccess =
    !fetchingAccessRequest &&
    !hasRequestedAccess &&
    (!accessRequest || accessRequest.status === "approved");

  let titleText = "Project not found";
  let descriptionText =
    "If this is a valid link, you can request access by clicking the button below.";

  if (isInvited) {
    titleText = "You've been invited to join this project!";
    descriptionText =
      "To accept, click the button below or open the link sent to your email.";
  } else if (hasRequestedAccess) {
    if (!accessRequest || accessRequest.status === "pending") {
      titleText = "Request Submitted";
      descriptionText =
        "Your request to access this project has been submitted. You will be notified by email once your request is approved. You may now close this dialog.";
    }
  }

  const getActionButton = () => {
    if (isInvited) {
      return (
        <AlertDialogAction
          className="bg-primary-0"
          onClick={acceptInvitation}
          disabled={accepting}
        >
          {accepting ? "Accepting..." : "Accept invitation"}
        </AlertDialogAction>
      );
    }

    if (canRequestAccess) {
      return (
        <AlertDialogAction className="bg-primary-0" onClick={requestAccess}>
          Request access
        </AlertDialogAction>
      );
    }

    let buttonText = "Request sent";
    if (accessRequest) {
      switch (accessRequest.status) {
        case "pending":
          buttonText = "Request sent";
          break;
        case "denied":
          buttonText = "Request denied";
          break;
        case "approved":
          buttonText = "Access granted";
          break;
        default:
          console.error(`Invalid accessRequest state: ${accessRequest}`);
          break;
      }
    }

    return (
      <AlertDialogAction className="bg-primary-0" disabled>
        {buttonText}
      </AlertDialogAction>
    );
  };

  return (
    <AlertDialog open={true}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{titleText}</AlertDialogTitle>
          <AlertDialogDescription>{descriptionText}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              navigate("/");
              if (props.onClose) props.onClose();
            }}
          >
            Go back
          </AlertDialogCancel>
          {getActionButton()}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default React.memo(UnauthorizedModal);
